import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { CogTileBlock } from './cog-directory';

/**
 * In-app native plugin that slices a Cloud Optimized GeoTIFF already on disk into
 * per-tile `z/x/y.webp` files. Implemented in `ios/App/App/CogUnpackPlugin.swift`
 * and `android/.../CogUnpackPlugin.java`; the web has no implementation, so the
 * download service streams and writes tiles from JS there instead.
 *
 * Blocks travel as a flat `[offset, length, z, x, y, ...]` array to keep the bridge
 * payload small for layers with tens of thousands of tiles.
 */
export interface CogUnpackOptions {
  /** Echoed in `progress` events, which are plugin-wide, so a caller can pick out its own */
  requestId: string;
  /** `file://` URI (or absolute path) of the COG on disk */
  cogPath: string;
  /** `file://` URI (or absolute path) of the layer directory the tiles go under */
  outDir: string;
  blocks: number[];
}

export interface CogUnpackResult {
  written: number;
  failed: number;
  /** Message of the first write that failed, when `failed > 0` */
  firstError?: string;
}

export interface CogUnpackProgress {
  /** The `requestId` of the `unpack` call this event belongs to */
  requestId: string;
  /** Tiles written so far in the current `unpack` call */
  written: number;
  total: number;
}

export interface CogUnpackPlugin {
  unpack(options: CogUnpackOptions): Promise<CogUnpackResult>;
  addListener(eventName: 'progress', listener: (progress: CogUnpackProgress) => void): Promise<PluginListenerHandle>;
}

export const COG_UNPACK_PLUGIN = 'CogUnpack';

const CogUnpack = registerPlugin<CogUnpackPlugin>(COG_UNPACK_PLUGIN);

export const cogUnpackAvailable = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(COG_UNPACK_PLUGIN);
};

export const flattenCogBlocks = (blocks: CogTileBlock[]): number[] => {
  const flat = new Array<number>(blocks.length * 5);
  let i = 0;
  for (const block of blocks) {
    flat[i++] = block.offset;
    flat[i++] = block.length;
    flat[i++] = block.z;
    flat[i++] = block.x;
    flat[i++] = block.y;
  }
  return flat;
};

export const unpackCogBlocks = (options: CogUnpackOptions): Promise<CogUnpackResult> => {
  return CogUnpack.unpack(options);
};

export const onCogUnpackProgress = (listener: (progress: CogUnpackProgress) => void): Promise<PluginListenerHandle> => {
  return CogUnpack.addListener('progress', listener);
};
